import api from '@shared/api';
import routes from '@root/routes';
import { PARAMETERS, EVENT_TYPES, EVENTS } from '@root/tracking/constants';
import { getSourcePageFrom } from '@root/tracking/helpers';
import { TourbookSummary, SearchListing, Listing, ErrorResponse } from '@root/types';
import { addListingToTourbook } from './createTourbookListing';

type SubmissionValues = {
  name: string;
  description?: string;
  listingId?: string;
  listing?: SearchListing | Listing;
};

type ReceiveNewTourbookAction = {
  type: 'RECEIVE_NEW_TOURBOOK';
  payload: {
    tourbook: TourbookSummary;
    meta: {
      analytics: {
        eventType: 'track';
        event: 'Tourbook Interaction';
        action: 'create tourbook';
        sourcePage: string;
        sourceContent: 'tourbook overlay';
        tourbookId: string;
        tourbookName: string;
        tourbookOwnerId: string;
        listingCount: number;
      };
    };
  };
};

export const receiveNewTourbook = (
  tourbook: TourbookSummary,
  sourcePage: string,
): ReceiveNewTourbookAction => ({
  type: 'RECEIVE_NEW_TOURBOOK',
  payload: {
    tourbook,
    meta: {
      analytics: {
        eventType: EVENT_TYPES.track,
        event: EVENTS.tourbookInteraction,
        action: PARAMETERS.createTourbook,
        sourcePage,
        sourceContent: PARAMETERS.tourbookOverlay,
        tourbookId: tourbook.id,
        tourbookName: tourbook.name,
        tourbookOwnerId: tourbook.ownerId,
        listingCount: tourbook.listingIds ? tourbook.listingIds.length : 0,
      },
    },
  },
});

type ErrorCreatingTourbookAction = {
  type: 'ERROR_CREATING_NEW_TOURBOOK';
  payload: SubmissionValues;
};

export const errorCreatingTourbook = (values: SubmissionValues): ErrorCreatingTourbookAction => ({
  type: 'ERROR_CREATING_NEW_TOURBOOK',
  payload: values,
});

export const createTourbook =
  (
    values: SubmissionValues,
    {
      onSuccess,
      onError,
    }: {
      onSuccess: (tourbook: TourbookSummary) => void;
      onError: (e: ErrorResponse) => void;
    } = {
      onSuccess: () => {},
      onError: () => {},
    },
  ) =>
  async (dispatch, getState) => {
    await api.post(routes.api.tourbooks('newest'), values).then(async response => {
      if (response.ok) {
        const tourbook: TourbookSummary = await response.json();
        dispatch(receiveNewTourbook(tourbook, getSourcePageFrom(getState())!));
        if (values.listing) {
          dispatch(
            addListingToTourbook(
              tourbook,
              getSourcePageFrom(getState())!,
              values.listing.analyticsInformation,
            ),
          );
        }
        onSuccess(tourbook);
      } else {
        const errorResponse: ErrorResponse = await response.json();
        dispatch(errorCreatingTourbook(values));
        onError(errorResponse);
      }
    });
  };

export type Actions = ReceiveNewTourbookAction | ErrorCreatingTourbookAction;
